import type { Schemas } from '@argzombie/api-client';
import { PANEL_POSES, PANEL_SHOTS, PANEL_TIERS } from '../../lib/story/panelIds';
import {
  OUTCOME_TONES,
  type AllyDef,
  type NpcDef,
  type PanelRulesDef,
  type PronounSetDef,
  type StoryRulesDef,
  type StoryToneMixDef,
} from '../../lib/story/types';
import { panelRulesDefaults, storyRulesDefaults } from '../../lib/storyDefaults';
import { withDefaults } from '../../lib/teamsDefaults';
import { issuesUnder, type FieldErrors } from '../../lib/validate';
import { TONE_COLORS } from '../../lib/story/ui';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
  smallBtn,
} from './fields';
import { useContentBundle, useContentRefs } from './refs';
import { CastLookField } from './CastLookField';
import type { FormProps } from './types';

type PanelTierDef = Schemas['PanelTierDef'];
type PanelShotRuleDef = Schemas['PanelShotRuleDef'];
type PanelPoseRuleDef = Schemas['PanelPoseRuleDef'];
type Story = Required<StoryRulesDef>;

type NumOpts = { integer?: boolean; min?: number; max?: number; step?: number; hint?: string };

/** The `story` block with its C# defaults filled in, nested blocks too (like `storyRules` in the validator). */
function storyWithDefaults(value: StoryRulesDef | null | undefined): Story {
  const d = storyRulesDefaults();
  const s = withDefaults(d, (value ?? {}) as Story);
  return {
    ...s,
    tone: {
      ...d.tone,
      ...s.tone,
      targetMix: { ...d.tone.targetMix, ...s.tone?.targetMix },
    },
    windfall: { ...d.windfall, ...s.windfall },
    trade: { ...d.trade, ...s.trade },
    ally: { ...d.ally, ...s.ally },
  };
}

/**
 * The `story` section (engine.html §3 "Starting numbers" and "Tone, windfalls and allies"): the engine numbers, the tone target mix,
 * the windfall caps, trade and the ally rules. Absent fields show the C# defaults (`storyRulesDefaults`), nested blocks too.
 */
export function StoryRulesForm({ value, onChange, errors }: FormProps<StoryRulesDef>) {
  const s = storyWithDefaults(value);
  const set = <K extends keyof Story>(k: K, v: Story[K]) => onChange({ ...s, [k]: v });
  const num = (k: keyof Story & string, label: string, o: NumOpts = {}) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min ?? 0}
      max={o.max}
      step={o.step}
      value={s[k] as number}
      issues={errors[k]}
      onChange={(v) => set(k, (v ?? 0) as never)}
    />
  );
  /** A number inside one of the nested blocks (`windfall`, `trade`, `ally`, `tone`). */
  const sub = <B extends 'windfall' | 'trade' | 'ally' | 'tone'>(
    block: B,
    k: keyof Story[B] & string,
    label: string,
    o: NumOpts = {},
  ) => (
    <NumberField
      key={`${block}.${k}`}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min ?? 0}
      max={o.max}
      step={o.step}
      value={(s[block] as Record<string, number>)[k]}
      issues={errors[`${block}.${k}`]}
      onChange={(v) => set(block, { ...s[block], [k]: v ?? 0 })}
    />
  );
  const mix = s.tone.targetMix as Required<StoryToneMixDef>;
  const sum = OUTCOME_TONES.reduce((a, t) => a + (mix[t] ?? 0), 0);
  const setMix = (t: (typeof OUTCOME_TONES)[number], v: number) =>
    set('tone', { ...s.tone, targetMix: { ...mix, [t]: v } });

  return (
    <>
      <Fieldset legend="Engine">
        {num('maxDepth', 'Max depth', {
          integer: true,
          min: 1,
          max: 4,
          hint: 'Choices on one path, 1..4. 4 is a hard cap in code.',
        })}
        {num('defaultTimerS', 'Default timer (s)', {
          min: 3,
          max: 20,
          hint: 'A beat with timer: true and no seconds. 3..20.',
        })}
        {num('relaxedTimerMult', 'Relaxed timer multiplier', {
          min: 1,
          max: 3,
          step: 0.1,
          hint: 'The "Relaxed choice timers" setting. 1..3.',
        })}
        {num('cellMinDwellMs', 'Cell min dwell (ms)', {
          integer: true,
          max: 1500,
          hint: 'Shortest time a cell stays up before a swipe moves on. 0..1500.',
        })}
        {num('maxCellsPerBeat', 'Max cells per beat', {
          integer: true,
          min: 1,
          max: 6,
          hint: 'Setup or consequence cells, not the choice cell. 1..6.',
        })}
        {num('maxChainsPerTicket', 'Max chains per ticket', {
          integer: true,
          max: 5,
          hint: 'Chains one encounter ticket may carry. 0..5.',
        })}
        {num('maxRollSheetEntries', 'Max roll sheet entries', {
          integer: true,
          min: 1,
          max: 200,
          hint: 'Reachable (beat, option) instances per chain; bounds the ticket. ≤ 200.',
        })}
        {num('maxStoryBytes', 'Max story bytes', {
          integer: true,
          min: 1024,
          max: 65536,
          hint: "Hard limit on a ticket's serialized story block. 1024..65536.",
        })}
        {num('carryTurnHours', 'Carry turn (hours)', {
          min: 0.25,
          max: 72,
          step: 0.25,
          hint: 'A turn that never fired in the raid: the companion turns this long after. 0.25..72.',
        })}
        {num('factSlackMs', 'Fact slack (ms)', {
          integer: true,
          max: 10000,
          hint: 'Timing slack when the server checks reveal times. 0..10000.',
        })}
        {num('echoChance', 'Echo chance', {
          max: 1,
          step: 0.05,
          hint: 'Chance an eligible ticket carries one echo chain. 0..1.',
        })}
        <SelectField
          label="Validation mode"
          value={s.validationMode}
          options={[
            { value: 'report', label: 'report (log to admin, never reject)' },
            { value: 'reject', label: 'reject (a failed story check loses the raid)' },
          ]}
          hint="Decision M9: keep report until the replay has proved itself in TestFlight."
          issues={errors.validationMode}
          onChange={(v) => set('validationMode', v ?? 'report')}
        />
        <CheckboxField
          label="Team triggers"
          hint="Card #309: chance events fire when any squad member walks into their room, not only the player's body."
          checked={s.teamTriggers ?? true}
          onChange={(v) => set('teamTriggers', v)}
        />
        {num('absentBadOutcomeWeight', 'Absent bad-outcome weight', {
          min: 0.1,
          max: 10,
          step: 0.1,
          hint: 'How much bad and disaster outcomes weigh when the player was not there at the trigger. 1 = unchanged. 0.1..10.',
        })}
      </Fieldset>

      <Fieldset legend="Tone">
        <div className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-wide">Target mix (%)</span>
          <p className="text-xs text-bruise">
            Reach-weighted target over all live chains, each 0..60. Guidance, not a gate.
          </p>
          <div className="mt-1 grid grid-cols-5 gap-2">
            {OUTCOME_TONES.map((t) => (
              <div key={t}>
                <span className="flex items-center gap-1 text-xs">
                  <span className="inline-block h-2 w-2" style={{ background: TONE_COLORS[t] }} />
                  {t}
                </span>
                <NumberInput
                  label={`Target ${t}`}
                  integer
                  min={0}
                  max={60}
                  value={mix[t]}
                  invalid={!!errors[`tone.targetMix.${t}`]}
                  onChange={(v) => setMix(t, v ?? 0)}
                />
                <IssueList issues={errors[`tone.targetMix.${t}`]} />
              </div>
            ))}
          </div>
          <div
            className="mt-2 flex h-3 overflow-hidden rounded border border-ink bg-white"
            aria-hidden
          >
            {OUTCOME_TONES.map((t) =>
              (mix[t] ?? 0) > 0 ? (
                <div
                  key={t}
                  style={{
                    background: TONE_COLORS[t],
                    width: `${(Math.max(0, mix[t] ?? 0) / Math.max(sum, 100)) * 100}%`,
                  }}
                />
              ) : null,
            )}
          </div>
          <p
            data-testid="tone-sum"
            className={`mt-0.5 font-mono text-xs ${sum === 100 ? 'text-bruise' : 'font-semibold text-blam'}`}
          >
            Sums to {sum}
            {sum === 100 ? '' : ' (must be 100)'}
          </p>
          <IssueList issues={errors['tone.targetMix']} />
        </div>
        {sub('tone', 'maxWindfallShare', 'Max windfall share (%)', {
          integer: true,
          max: 100,
          hint: "Warns when more of a chain's reach than this is windfall. 0..100.",
        })}
      </Fieldset>

      <Fieldset legend="Windfall caps (item value)">
        {sub('windfall', 'valuePerRunDay', 'Budget per run day', {
          integer: true,
          max: 1000,
          hint: 'Beat windfall budget per survival day, reserved at issue. 0..1000.',
        })}
        {sub('windfall', 'stashValueMax', 'Stash max', {
          integer: true,
          max: 400,
          hint: 'One reveal_stash. 0..400.',
        })}
        {sub('windfall', 'tradeBonusValueMax', 'Trade bonus max', {
          integer: true,
          max: 200,
          hint: 'The generous trader\'s "and then some". 0..200.',
        })}
        {sub('windfall', 'homeValueMax', 'Home gift max', {
          integer: true,
          max: 200,
          hint: 'give_items to home, per outcome. 0..200.',
        })}
      </Fieldset>

      <Fieldset legend="Trade">
        {sub('trade', 'shortPct', 'Short trade (%)', {
          integer: true,
          min: 30,
          max: 70,
          hint: 'A short trade delivers this share of each promised stack. 30..70.',
        })}
      </Fieldset>

      <Fieldset legend="Allies">
        {sub('ally', 'maxActive', 'Max active', {
          integer: true,
          min: 1,
          max: 4,
          hint: 'Active allies per run; over it, the effect is a ledger tag only. 1..4.',
        })}
        {sub('ally', 'deliveries', 'Deliveries', {
          integer: true,
          max: 5,
          hint: 'Per new ally; each ally def can set fewer. 0..5.',
        })}
        {sub('ally', 'everyHours', 'Every (hours)', {
          min: 6,
          max: 72,
          hint: 'Time between deliveries. 6..72.',
        })}
        {sub('ally', 'jitterHours', 'Jitter (hours)', {
          max: 12,
          hint: "Rolled jitter so they don't land like clockwork. 0..12.",
        })}
        {sub('ally', 'deliveryValueMax', 'Delivery value max', {
          integer: true,
          min: 10,
          max: 100,
          hint: "One delivery's value. 10..100.",
        })}
        {sub('ally', 'echoAfterRaids', 'Echo after raids', {
          integer: true,
          min: 1,
          max: 10,
          hint: "Resolved raids before an ally's echo chain can be offered. 1..10.",
        })}
      </Fieldset>
    </>
  );
}

const tierDefaults = (): PanelTierDef => ({ id: 'high', width: 1280, height: 960, samples: 4 });
const shotDefaults = (): PanelShotRuleDef => ({ shot: 'medium', fill: 0.95, fov: 30 });
const poseRowDefaults = (): PanelPoseRuleDef => ({
  pose: 'stand',
  variants: ['clip:Pose_stand@0'],
});

/** The `panelRules` section (panels.html §2): actor and balloon caps, layout, quality tiers and per-shot framing. */
export function PanelRulesForm({ value, onChange, errors }: FormProps<PanelRulesDef>) {
  const r = withDefaults(panelRulesDefaults(), (value ?? {}) as Required<PanelRulesDef>);
  const set = <K extends keyof PanelRulesDef>(k: K, v: PanelRulesDef[K]) =>
    onChange({ ...r, [k]: v });
  const num = (k: keyof PanelRulesDef & string, label: string, o: NumOpts = {}) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min ?? 0}
      max={o.max}
      step={o.step}
      value={r[k] as number}
      issues={errors[k]}
      onChange={(v) => set(k, (v ?? 0) as never)}
    />
  );
  return (
    <>
      <Fieldset legend="Cells">
        {num('maxActors', 'Max actors', { integer: true, min: 1, max: 6, hint: 'Per cell. 1..6.' })}
        {num('maxBalloonsPerCell', 'Max balloons per cell', {
          integer: true,
          max: 3,
          hint: '0..3, each at most 90 characters.',
        })}
        {num('gutterFrac', 'Gutter', {
          max: 0.1,
          step: 0.005,
          hint: 'Share of the page width (at least 8 px). 0..0.1.',
        })}
        {num('swipeFrac', 'Swipe threshold', {
          max: 1,
          step: 0.01,
          hint: 'Share of the width a swipe must pass. Between 0 and 1.',
        })}
        {num('lowTierFirstCellMs', 'Low tier first cell (ms)', {
          integer: true,
          hint: "Budget for the first cell's render on the low tier.",
        })}
        {num('cacheCells', 'Cached cells', {
          integer: true,
          min: 1,
          max: 16,
          hint: 'Rendered cells kept in memory. 1..16.',
        })}
      </Fieldset>
      <Fieldset legend="Quality tiers">
        <ListEditor<PanelTierDef>
          label="Tiers"
          what="tier"
          hint="Render size and supersampling per tier. Empty = the code defaults."
          items={r.tiers ?? []}
          make={tierDefaults}
          onChange={(next) => set('tiers', next)}
          issues={errors.tiers}
          row={(t, j, setT) => (
            <div className="grid grid-cols-4 gap-2">
              <SelectField
                label="Tier"
                value={t.id}
                options={PANEL_TIERS.map((x) => ({ value: x, label: x }))}
                issues={errors[`tiers.${j}.id`]}
                onChange={(v) => setT({ ...t, id: v ?? '' })}
              />
              <NumberField
                label="Width"
                integer
                min={64}
                value={t.width}
                issues={errors[`tiers.${j}.width`]}
                onChange={(v) => setT({ ...t, width: v ?? 0 })}
              />
              <NumberField
                label="Height"
                integer
                min={48}
                value={t.height}
                onChange={(v) => setT({ ...t, height: v ?? 0 })}
              />
              <SelectField
                label="Samples"
                value={String(t.samples ?? 1)}
                options={[1, 2, 4, 8].map((x) => ({ value: String(x), label: `${x}×` }))}
                issues={errors[`tiers.${j}.samples`]}
                onChange={(v) => setT({ ...t, samples: Number(v ?? 1) })}
              />
            </div>
          )}
        />
      </Fieldset>
      <Fieldset legend="Shot framing">
        <ListEditor<PanelShotRuleDef>
          label="Shots"
          what="shot"
          hint="How much of the cell the subject fills and the starting FOV. Empty = the code defaults."
          items={r.shots ?? []}
          make={shotDefaults}
          onChange={(next) => set('shots', next)}
          issues={errors.shots}
          row={(x, j, setX) => (
            <div className="grid grid-cols-3 gap-2">
              <SelectField
                label="Shot"
                value={x.shot}
                options={PANEL_SHOTS.map((v) => ({ value: v, label: v }))}
                issues={errors[`shots.${j}.shot`]}
                onChange={(v) => setX({ ...x, shot: v ?? '' })}
              />
              <NumberField
                label="Fill"
                max={1}
                step={0.05}
                hint="(0..1]"
                value={x.fill}
                issues={errors[`shots.${j}.fill`]}
                onChange={(v) => setX({ ...x, fill: v ?? 0 })}
              />
              <NumberField
                label="FOV (°)"
                min={10}
                max={50}
                hint="10..50"
                value={x.fov}
                issues={errors[`shots.${j}.fov`]}
                onChange={(v) => setX({ ...x, fov: v ?? 0 })}
              />
            </div>
          )}
        />
      </Fieldset>
      {/* Card #303: what each named pose samples (variant 0 = the default, the rest are an actor's poseVariant). */}
      <Fieldset legend="Poses">
        <ListEditor<PanelPoseRuleDef>
          label="Pose frames"
          what="pose"
          hint="Clip frames per named pose (clip:<clipId>@<0..1>, optional :mirror). The first is the default; a pose left out plays its own take."
          items={r.poses ?? []}
          make={poseRowDefaults}
          onChange={(next) => set('poses', next)}
          issues={errors.poses}
          row={(x, j, setX) => (
            <div className="grid grid-cols-3 gap-2">
              <SelectField
                label="Pose"
                value={x.pose}
                options={PANEL_POSES.map((v) => ({ value: v, label: v }))}
                issues={errors[`poses.${j}.pose`]}
                onChange={(v) => setX({ ...x, pose: v ?? '' })}
              />
              <StringListEditor
                label="Variants"
                value={x.variants ?? []}
                onChange={(next) => setX({ ...x, variants: next })}
                errors={errors}
                base={`poses.${j}.variants`}
                placeholder="clip:Pose_stand@0"
              />
              <StringListEditor
                label="Zombie variants"
                value={x.zombieVariants ?? []}
                onChange={(next) => setX({ ...x, zombieVariants: next })}
                errors={errors}
                base={`poses.${j}.zombieVariants`}
                placeholder="clip:Z_Lunge@0.4"
              />
            </div>
          )}
        />
      </Fieldset>
    </>
  );
}

/** shared-cs `AllyDef` defaults (not `newAlly`, whose name is a placeholder for Add). */
const allyDefaults = (): Required<AllyDef> => ({
  id: '',
  name: '',
  look: 'from_role',
  appearance: null,
  deliveries: { count: 3, everyHours: 24, table: '', valueMax: 40 },
  echoes: [],
  factionId: null,
});

/** An ally (`allies[]`, engine.html §5 "story_allies and ally_deliveries"): who they are, what they send home, and their echoes. */
export function AllyForm({ value, onChange, errors }: FormProps<AllyDef>) {
  const a = withDefaults(allyDefaults(), value ?? {});
  const d = { ...allyDefaults().deliveries, ...a.deliveries };
  const bundle = useContentBundle();
  const { lootTables } = useContentRefs();
  const set = <K extends keyof AllyDef>(k: K, v: AllyDef[K]) => onChange({ ...a, [k]: v });
  const setD = (k: keyof typeof d, v: number | string) => set('deliveries', { ...d, [k]: v });
  const cap = storyWithDefaults(bundle.story).ally.deliveryValueMax;
  const chains = (bundle.beatChains ?? []).filter((c) => c?.id);
  const echoTagged = (tags?: string[] | null) => (tags ?? []).includes('echo');
  const chainIds = [
    ...chains.filter((c) => echoTagged(c!.tags)),
    ...chains.filter((c) => !echoTagged(c!.tags)),
  ].map((c) => ({ id: c!.id!, echo: echoTagged(c!.tags), title: c!.title ?? '' }));
  const echoes = a.echoes ?? [];
  const unknown = echoes.filter((e) => !chainIds.some((c) => c.id === e));
  const toggle = (id: string) =>
    set('echoes', echoes.includes(id) ? echoes.filter((e) => e !== id) : [...echoes, id]);
  const chip = (on: boolean, bad = false) =>
    `rounded-full border-2 px-2 py-0.5 text-xs font-mono ${
      bad ? 'border-blam text-blam' : on ? 'border-ink bg-benday' : 'border-newsprint bg-white'
    }`;

  return (
    <>
      <Fieldset legend="Ally">
        <TextField label="Id" mono value={a.id} issues={errors.id} onChange={(v) => set('id', v)} />
        <TextField
          label="Name"
          value={a.name}
          issues={errors.name}
          onChange={(v) => set('name', v)}
        />
        <CastLookField
          value={(a as { appearance?: string | null }).appearance}
          issues={errors.appearance}
          onChange={(v) => onChange({ ...a, appearance: v } as AllyDef)}
        />
        <Field
          label="Look"
          hint="from_role = the bound NPC's own look seed; or a fixed look id."
          issues={errors.look}
        >
          {(id, invalid) => (
            <div className="flex gap-2">
              <input
                id={id}
                aria-invalid={invalid}
                className="w-full rounded border-2 border-ink bg-white px-2 py-1 font-mono text-sm"
                value={a.look ?? ''}
                onChange={(e) => set('look', e.target.value)}
              />
              {a.look !== 'from_role' && (
                <button type="button" className={smallBtn} onClick={() => set('look', 'from_role')}>
                  from_role
                </button>
              )}
            </div>
          )}
        </Field>
        <div className="text-xs text-bruise">
          <span className="block font-semibold uppercase tracking-wide text-ink">Faction</span>
          Reserved for faction reputation (later): {a.factionId ?? 'none'}. Edit JSON to set it.
        </div>
      </Fieldset>
      <Fieldset legend="Deliveries">
        <NumberField
          label="Count"
          integer
          min={0}
          max={5}
          hint="Crates sent home. 0..5 (0 = none)."
          value={d.count}
          issues={errors['deliveries.count']}
          onChange={(v) => setD('count', v ?? 0)}
        />
        <NumberField
          label="Every (hours)"
          min={6}
          max={72}
          hint="6..72, plus story.ally.jitterHours."
          value={d.everyHours}
          issues={errors['deliveries.everyHours']}
          onChange={(v) => setD('everyHours', v ?? 0)}
        />
        <SelectField
          label="Loot table"
          value={d.table}
          options={lootTables.map((t) => ({ value: t.id ?? '', label: t.id ?? '' }))}
          issues={errors['deliveries.table']}
          onChange={(v) => setD('table', v ?? '')}
        />
        <NumberField
          label="Value max"
          integer
          min={10}
          max={100}
          hint={`10..100. The server also caps each delivery at story.ally.deliveryValueMax (${cap}).`}
          value={d.valueMax}
          issues={errors['deliveries.valueMax']}
          onChange={(v) => setD('valueMax', v ?? 0)}
        />
      </Fieldset>
      <Fieldset legend="Echoes">
        <div className="sm:col-span-2">
          <p className="text-xs text-bruise">
            Beat chains this ally can bring back (after story.ally.echoAfterRaids raids). Chains
            tagged echo come first.
          </p>
          {chainIds.length === 0 && unknown.length === 0 && (
            <p className="text-xs text-bruise">No beat chains in the draft.</p>
          )}
          <div className="mt-1 flex flex-wrap gap-1" role="group" aria-label="Echo chains">
            {chainIds.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={echoes.includes(c.id)}
                title={c.title}
                className={chip(echoes.includes(c.id))}
                onClick={() => toggle(c.id)}
              >
                {c.id}
                {c.echo ? ' · echo' : ''}
              </button>
            ))}
            {unknown.map((e) => (
              <button
                key={e}
                type="button"
                aria-pressed
                className={chip(true, true)}
                title="Not in the draft: click to remove"
                onClick={() => toggle(e)}
              >
                {e} ✕
              </button>
            ))}
          </div>
          <IssueList issues={issuesUnder(errors, 'echoes')} />
        </div>
      </Fieldset>
    </>
  );
}

const PRONOUN_FIELDS = ['they', 'them', 'their', 'theirs', 'themself'] as const;
const PRONOUN_SAMPLE = '{They} said {they} would bring {their} own.';

/** Renders `{they}`/`{They}`-style tokens with a pronoun set (capitalised token = capitalised word). Unknown tokens stay. */
function renderPronouns(text: string, p: PronounSetDef): string {
  return text.replace(/\{([A-Za-z]+)\}/g, (whole, token: string) => {
    const key = token.toLowerCase() as (typeof PRONOUN_FIELDS)[number];
    if (!PRONOUN_FIELDS.includes(key)) return whole;
    const word = p[key] || `{${key}}`;
    return token[0] === token[0]!.toUpperCase() ? word[0]!.toUpperCase() + word.slice(1) : word;
  });
}

/** A pronoun set (`pronounSets[]`, panels.html §4): the five forms and plural verb agreement, with a live sample. */
export function PronounSetForm({ value, onChange, errors }: FormProps<PronounSetDef>) {
  const p: PronounSetDef = {
    id: '',
    they: '',
    them: '',
    their: '',
    theirs: '',
    themself: '',
    plural: false,
    ...value,
  };
  const set = <K extends keyof PronounSetDef>(k: K, v: PronounSetDef[K]) =>
    onChange({ ...p, [k]: v });
  return (
    <Fieldset legend="Pronoun set">
      <TextField label="Id" mono value={p.id} issues={errors.id} onChange={(v) => set('id', v)} />
      {PRONOUN_FIELDS.map((f) => (
        <TextField key={f} label={f} value={p[f]} issues={errors[f]} onChange={(v) => set(f, v)} />
      ))}
      <CheckboxField
        label="Plural verbs"
        hint="{x.verb:is|are} takes the second form (like they)."
        checked={!!p.plural}
        onChange={(v) => set('plural', v)}
      />
      <div className="sm:col-span-2 rounded border border-newsprint bg-newsprint/30 p-2 text-sm">
        <span className="block text-xs text-bruise">Sample: {PRONOUN_SAMPLE}</span>
        <span data-testid="pronoun-sample">{renderPronouns(PRONOUN_SAMPLE, p)}</span>
      </div>
    </Fieldset>
  );
}

/** A named NPC (`npcs[]`) a beat can cast with `bind: npc` (`{npc:doc.name}` in templates). */
export function NpcForm({ value, onChange, errors }: FormProps<NpcDef>) {
  const n: NpcDef = {
    id: '',
    name: '',
    archetype: '',
    pronouns: 'they',
    lookSeed: 0,
    wear: [],
    ...value,
  };
  const bundle = useContentBundle();
  const set = <K extends keyof NpcDef>(k: K, v: NpcDef[K]) => onChange({ ...n, [k]: v });
  const sets = (bundle.pronounSets ?? []).map((s) => s?.id ?? '').filter((s) => s !== '');
  const pronounOptions = (sets.length ? sets : ['they', 'she', 'he']).map((s) => ({
    value: s,
    label: s,
  }));
  return (
    <Fieldset legend="NPC">
      <TextField label="Id" mono value={n.id} issues={errors.id} onChange={(v) => set('id', v)} />
      <TextField
        label="Name"
        value={n.name}
        issues={errors.name}
        onChange={(v) => set('name', v)}
      />
      <TextField
        label="Archetype"
        hint="Free text for writers (doc, trader, kid...)."
        value={n.archetype}
        onChange={(v) => set('archetype', v)}
      />
      <Field
        label="Pronouns"
        hint={sets.length ? undefined : 'No pronoun sets in the draft: the built-in they/she/he.'}
        issues={errors.pronouns}
      >
        {(id, invalid) => (
          <SelectInput
            id={id}
            invalid={invalid}
            value={n.pronouns}
            options={pronounOptions}
            onChange={(v) => set('pronouns', v ?? 'they')}
          />
        )}
      </Field>
      <NumberField
        label="Look seed"
        integer
        min={0}
        hint="0 = derived from the id."
        value={n.lookSeed}
        issues={errors.lookSeed}
        onChange={(v) => set('lookSeed', v ?? 0)}
      />
      <StringListEditor
        label="Wear"
        hint="Worn model ids layered on the look."
        value={n.wear ?? []}
        errors={errors as FieldErrors}
        base="wear"
        onChange={(next) => set('wear', next)}
      />
      <CastLookField
        value={(n as { appearance?: string | null }).appearance}
        issues={errors.appearance}
        onChange={(v) => onChange({ ...n, appearance: v } as NpcDef)}
      />
    </Fieldset>
  );
}
